import React, { useEffect, useState } from 'react';
import { getDoctors } from '../api';
import axios from 'axios';
import styles from './Page.module.css';

const API = '/api';

export default function UserManagementPage() {
  const [users, setUsers] = useState([]);
  const [doctors, setDoctors] = useState([]);
  const [form, setForm] = useState({ username: '', password: '', role: 'RECEPTIONIST', doctorId: '' });
  const [loading, setLoading] = useState(false);
  const [msg, setMsg] = useState('');

  // Edit modal state
  const [editUser, setEditUser] = useState(null);
  const [editForm, setEditForm] = useState({ password: '', role: '', doctorId: '' });
  const [editLoading, setEditLoading] = useState(false);
  const [editMsg, setEditMsg] = useState('');

  useEffect(() => { fetchAll(); }, []);

  const fetchAll = async () => {
    const [u, d] = await Promise.all([
      axios.get(`${API}/auth/users`),
      getDoctors(),
    ]);
    setUsers(u.data);
    setDoctors(d.data);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setMsg('');
    try {
      await axios.post(`${API}/auth/register`, {
        username: form.username,
        password: form.password,
        role: form.role,
        doctorId: form.role === 'DOCTOR' ? parseInt(form.doctorId) : null,
      });
      setMsg('User created successfully!');
      setForm({ username: '', password: '', role: 'RECEPTIONIST', doctorId: '' });
      fetchAll();
    } catch {
      setMsg('Failed. Username may already exist.');
    }
    setLoading(false);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this user?')) return;
    await axios.delete(`${API}/auth/users/${id}`);
    fetchAll();
  };

  const openEdit = (u) => {
    setEditUser(u);
    setEditForm({ password: '', role: u.role, doctorId: u.doctorId || '' });
    setEditMsg('');
  };

  const closeEdit = () => {
    setEditUser(null);
    setEditForm({ password: '', role: '', doctorId: '' });
    setEditMsg('');
  };

  const handleEditSubmit = async () => {
    setEditLoading(true);
    setEditMsg('');
    try {
      await axios.put(`${API}/auth/users/${editUser.id}`, {
        password: editForm.password || null,
        role: editForm.role,
        doctorId: editForm.role === 'DOCTOR' ? parseInt(editForm.doctorId) : null,
      });
      setEditMsg('User updated successfully!');
      fetchAll();
      setTimeout(() => closeEdit(), 1200);
    } catch {
      setEditMsg('Failed to update user.');
    }
    setEditLoading(false);
  };

  const roleColor = (r) => {
    switch (r) {
      case 'ADMIN':        return { bg: '#fce4e4', color: '#c0272d' };
      case 'RECEPTIONIST': return { bg: '#e3f2fd', color: '#1565c0' };
      case 'DOCTOR':       return { bg: '#e8f5e9', color: '#2e7d32' };
      default:             return { bg: '#f5f5f5', color: '#888' };
    }
  };

  const displayRole = (r) => r === 'RECEPTIONIST' ? 'FRONT OFFICE' : r;

  return (
    <div>
      <h2 className={styles.heading}>User Management</h2>

      <div className={styles.grid}>
        {/* Create User Form */}
        <div className={styles.card}>
          <h3>Create New User</h3>
          <form onSubmit={handleSubmit}>
            <div className={styles.field}>
              <label>Username *</label>
              <input type="text" value={form.username}
                placeholder="e.g. dr.sharma"
                onChange={e => setForm({ ...form, username: e.target.value })}
                required />
            </div>
            <div className={styles.field}>
              <label>Password *</label>
              <input type="password" value={form.password}
                placeholder="Enter password"
                onChange={e => setForm({ ...form, password: e.target.value })}
                required />
            </div>
            <div className={styles.field}>
              <label>Role *</label>
              <select value={form.role}
                onChange={e => setForm({ ...form, role: e.target.value, doctorId: '' })}>
                <option value="RECEPTIONIST">Front Office</option>
                <option value="DOCTOR">Doctor</option>
                <option value="ADMIN">Admin</option>
              </select>
            </div>

            {form.role === 'DOCTOR' && (
              <div className={styles.field}>
                <label>Link to Doctor *</label>
                <select value={form.doctorId}
                  onChange={e => setForm({ ...form, doctorId: e.target.value })}
                  required>
                  <option value="">Select Doctor</option>
                  {doctors.map(d => (
                    <option key={d.id} value={d.id}>
                      {d.name} - {d.specialization}
                    </option>
                  ))}
                </select>
              </div>
            )}

            {msg && (
              <p style={{
                fontSize: 13, fontWeight: 500, marginBottom: 10,
                color: msg.includes('success') ? '#2e7d32' : '#c0272d',
              }}>
                {msg}
              </p>
            )}

            <button type="submit" className={styles.btn} disabled={loading}>
              {loading ? 'Creating...' : 'Create User'}
            </button>
          </form>
        </div>

        {/* Users List */}
        <div className={styles.card}>
          <h3>All Users ({users.length})</h3>
          <table className={styles.table}>
            <thead>
              <tr>
                <th>#</th>
                <th>Username</th>
                <th>Role</th>
                <th>Doctor</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {users.map((u, i) => (
                <tr key={u.id}>
                  <td>{i + 1}</td>
                  <td><strong>{u.username}</strong></td>
                  <td>
                    <span style={{
                      padding: '3px 10px', borderRadius: 20,
                      fontSize: 12, fontWeight: 700,
                      background: roleColor(u.role).bg,
                      color: roleColor(u.role).color,
                    }}>
                      {displayRole(u.role)}
                    </span>
                  </td>
                  <td>
                    {u.doctorId
                      ? doctors.find(d => d.id === u.doctorId)?.name || `ID: ${u.doctorId}`
                      : '—'}
                  </td>
                  <td>
                    <div style={{ display: 'flex', gap: 6 }}>
                      <button className={styles.editBtn}
                        onClick={() => openEdit(u)}>
                        Edit
                      </button>
                      <button className={styles.deleteBtn}
                        onClick={() => handleDelete(u.id)}>
                        Delete
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Edit Modal */}
      {editUser && (
        <div style={{
          position: 'fixed', inset: 0,
          background: 'rgba(0,0,0,0.5)',
          display: 'flex', alignItems: 'center',
          justifyContent: 'center', zIndex: 1000,
        }}>
          <div style={{
            background: '#fff', borderRadius: 14,
            padding: 28, width: '100%', maxWidth: 420,
            boxShadow: '0 8px 32px rgba(0,0,0,0.2)',
          }}>
            <h3 style={{ color: '#1a3a6b', marginBottom: 16 }}>
              Edit User — <span style={{ color: '#c0272d' }}>{editUser.username}</span>
            </h3>

            {/* Reset Password */}
            <div className={styles.field}>
              <label>New Password <span style={{ color: '#aaa', fontWeight: 400 }}>(leave blank to keep current)</span></label>
              <input
                type="password"
                value={editForm.password}
                placeholder="Enter new password"
                onChange={e => setEditForm({ ...editForm, password: e.target.value })}
              />
            </div>

            {/* Change Role */}
            <div className={styles.field}>
              <label>Role *</label>
              <select value={editForm.role}
                onChange={e => setEditForm({ ...editForm, role: e.target.value, doctorId: '' })}>
                <option value="RECEPTIONIST">Front Office</option>
                <option value="DOCTOR">Doctor</option>
                <option value="ADMIN">Admin</option>
              </select>
            </div>

            {/* Link Doctor if role is DOCTOR */}
            {editForm.role === 'DOCTOR' && (
              <div className={styles.field}>
                <label>Link to Doctor *</label>
                <select value={editForm.doctorId}
                  onChange={e => setEditForm({ ...editForm, doctorId: e.target.value })}>
                  <option value="">Select Doctor</option>
                  {doctors.map(d => (
                    <option key={d.id} value={d.id}>
                      {d.name} - {d.specialization}
                    </option>
                  ))}
                </select>
              </div>
            )}

            {editMsg && (
              <p style={{
                fontSize: 13, fontWeight: 500, marginBottom: 10,
                color: editMsg.includes('success') ? '#2e7d32' : '#c0272d',
              }}>
                {editMsg}
              </p>
            )}

            <div style={{ display: 'flex', gap: 10, marginTop: 8 }}>
              <button
                onClick={handleEditSubmit}
                disabled={editLoading}
                style={{
                  flex: 1, background: '#1a3a6b', color: '#fff',
                  border: 'none', borderRadius: 8, padding: '10px',
                  fontSize: 14, fontWeight: 700, cursor: 'pointer',
                }}>
                {editLoading ? 'Saving...' : 'Save Changes'}
              </button>
              <button
                onClick={closeEdit}
                style={{
                  flex: 1, background: 'transparent', color: '#1a3a6b',
                  border: '1.5px solid #1a3a6b', borderRadius: 8,
                  padding: '10px', fontSize: 14, fontWeight: 700, cursor: 'pointer',
                }}>
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
